import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTelegram } from '@/hooks/useTelegram';
import { useAuth } from '@/hooks/useAuth';
import {
  getNotificationSettings,
  updateNotificationSettings,
  sendTestNotification,
} from '@/services/notifications';

interface SettingsPageProps {
  onClose: () => void;
}

export function SettingsPage({ onClose }: SettingsPageProps) {
  const { user } = useAuth();
  const { hapticFeedback } = useTelegram();
  
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testSent, setTestSent] = useState(false);
  
  useEffect(() => {
    loadSettings();
  }, []);
  
  const loadSettings = async () => {
    try {
      const settings = await getNotificationSettings();
      setNotificationsEnabled(settings.notificationsEnabled);
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleToggleNotifications = async () => {
    try {
      setIsSaving(true);
      const newValue = !notificationsEnabled;
      await updateNotificationSettings(newValue);
      setNotificationsEnabled(newValue);
      hapticFeedback('light');
    } catch (error) {
      console.error('Failed to update settings:', error);
      hapticFeedback('error');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleTestNotification = async () => {
    try {
      setIsTesting(true);
      await sendTestNotification();
      setTestSent(true);
      hapticFeedback('success');
      
      setTimeout(() => setTestSent(false), 3000);
    } catch (error) {
      console.error('Failed to send test notification:', error);
      hapticFeedback('error');
    } finally {
      setIsTesting(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-primary-950 flex flex-col safe-area-top safe-area-bottom">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-primary-800/50">
        <button onClick={onClose} className="text-primary-400">
          ← Назад
        </button>
        <h1 className="text-lg font-semibold text-white">Настройки</h1>
        <div className="w-16" />
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Profile */}
            <section>
              <h2 className="text-sm font-medium text-primary-400 mb-3">Профиль</h2>
              <div className="bg-primary-800/50 rounded-xl p-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-primary-700 flex items-center justify-center">
                    <span className="text-2xl">👤</span>
                  </div>
                  <div>
                    <p className="font-medium text-white">
                      {user?.firstName} {user?.lastName}
                    </p>
                    {user?.username && (
                      <p className="text-sm text-primary-400">@{user.username}</p>
                    )}
                  </div>
                </div>
              </div>
            </section>
            
            {/* Notifications */}
            <section>
              <h2 className="text-sm font-medium text-primary-400 mb-3">Уведомления</h2>
              <div className="bg-primary-800/50 rounded-xl divide-y divide-primary-700/50">
                {/* Toggle */}
                <div className="p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-white">Push-уведомления</p>
                    <p className="text-sm text-primary-400">
                      Напоминания о практиках и событиях
                    </p>
                  </div>
                  <button
                    onClick={handleToggleNotifications}
                    disabled={isSaving}
                    className={`w-12 h-7 rounded-full transition-colors relative ${
                      notificationsEnabled ? 'bg-green-500' : 'bg-primary-600'
                    }`}
                  >
                    <motion.div
                      animate={{ x: notificationsEnabled ? 20 : 2 }}
                      className="w-6 h-6 bg-white rounded-full absolute top-0.5"
                    />
                  </button>
                </div>
                
                {/* Test */}
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-white">Тестовое уведомление</p>
                      <p className="text-sm text-primary-400">
                        Проверь, что уведомления работают
                      </p>
                    </div>
                    <button
                      onClick={handleTestNotification}
                      disabled={isTesting || !notificationsEnabled}
                      className="px-4 py-2 bg-primary-700 text-primary-200 text-sm rounded-lg disabled:opacity-50"
                    >
                      {isTesting ? '...' : testSent ? '✓ Отправлено' : 'Отправить'}
                    </button>
                  </div>
                </div>
              </div>
              
              {notificationsEnabled && (
                <p className="text-xs text-primary-500 mt-2 px-1">
                  📱 Уведомления приходят в Telegram: утром (8:00), вечером (20:00 если не практиковал), 
                  за 30 мин до событий, при истечении квестов.
                </p>
              )}
            </section>
            
            {/* About */}
            <section>
              <h2 className="text-sm font-medium text-primary-400 mb-3">О приложении</h2>
              <div className="bg-primary-800/50 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-primary-400">Версия</span>
                  <span className="text-primary-200">1.0.0</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-primary-400">Разработчик</span>
                  <span className="text-primary-200">На краю земли</span>
                </div>
              </div>
            </section>
            
            {/* Links */}
            <section>
              <h2 className="text-sm font-medium text-primary-400 mb-3">Ссылки</h2>
              <div className="space-y-2">
                <a
                  href="https://t.me/your_support_bot"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-primary-800/50 rounded-xl p-4 text-primary-200 hover:bg-primary-800/70 transition-colors"
                >
                  💬 Поддержка
                </a>
                <a
                  href="https://your-site.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-primary-800/50 rounded-xl p-4 text-primary-200 hover:bg-primary-800/70 transition-colors"
                >
                  📄 Политика конфиденциальности
                </a>
                <a
                  href="https://your-site.com/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-primary-800/50 rounded-xl p-4 text-primary-200 hover:bg-primary-800/70 transition-colors"
                >
                  📋 Условия использования
                </a>
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
